/**
 * #2494: the DoD of a Goal whose `dod` copies its lens conditions is edited
 * row by row. Each row is a condition named by ref; the save sends operations
 * (rewrite, add, delete) with the lens version the rows were read at.
 */

export interface GoalDodRow {
  condition_ref: string;
  text: string;
  /** A row tied to a judgment. Rewriting or deleting it may ask 「保留にして直しますか」 (#2537). */
  judgment: boolean;
  /** A time choice row: shown, but neither rewritten nor deleted. */
  locked?: boolean;
}

export interface GoalDodDraftRow {
  /** Null for a row added in this edit. */
  condition_ref: string | null;
  text: string;
  judgment: boolean;
  locked?: boolean;
}

export type GoalDodEdit =
  | { op: 'rewrite'; condition_ref: string; text: string }
  | { op: 'add'; text: string }
  | { op: 'delete'; condition_ref: string };

export function draftRowsFrom(rows: GoalDodRow[]): GoalDodDraftRow[] {
  return rows.map((row) => ({ condition_ref: row.condition_ref, text: row.text, judgment: row.judgment, locked: row.locked === true }));
}

/**
 * The operations that turn the read rows into the draft. A row emptied by the
 * principal counts as deleted; an added row left empty is dropped. Locked
 * (time choice) rows never produce an operation.
 */
export function dodRowEdits(original: GoalDodRow[], draft: GoalDodDraftRow[]): GoalDodEdit[] {
  const edits: GoalDodEdit[] = [];
  const kept = new Map<string, string>();
  for (const row of draft) {
    const text = row.text.trim();
    if (row.condition_ref === null) {
      if (text) edits.push({ op: 'add', text });
      continue;
    }
    if (text) kept.set(row.condition_ref, text);
  }
  for (const row of original) {
    if (row.locked) continue;
    const text = kept.get(row.condition_ref);
    if (text === undefined) edits.push({ op: 'delete', condition_ref: row.condition_ref });
    else if (text !== row.text.trim()) edits.push({ op: 'rewrite', condition_ref: row.condition_ref, text });
  }
  return [...edits.filter((edit) => edit.op !== 'add'), ...edits.filter((edit) => edit.op === 'add')];
}
